import type { ResolveRow } from "./resolveWithValue.helpers";

import { getFieldLabel, type SkipGroup } from "./DiscrepancyReviewDialog.helpers";

// ---------------------------------------------------------------------------
// Document-target eligibility (mirrors assertEligibleForDocumentUpdate).
// ---------------------------------------------------------------------------

/**
 * Every field a DOCUMENT-target write can reach. Mirrors
 * isDocumentWritableField server-side; keep both in sync manually.
 */
export const DOCUMENT_WRITABLE_FIELDS: ReadonlySet<string> = new Set([
  // Manifest-level (8)
  "generatorName",
  "generatorId",
  "generatorStreetAddress",
  "generatorStreet2",
  "generatorCity",
  "generatorState",
  "generatorZip",
  "destinationFacilityId",
  // Per-line (7). quantity is compared in pounds; the server converts it back
  // to the document line's own unit before writing.
  "quantity",
  "managementMethodCode",
  "federalWasteCodes",
  "stateWasteCodes",
  "containerTypeCode",
  "dotPrintedInformation",
  "nonHazWasteDescription",
]);

export interface DocumentEligibility {
  eligibleIds: string[];
  skips: SkipGroup[];
}

/** The subset of ResolveRow this eligibility check needs — narrow enough that "apply to others" matching rows satisfy it too. */
export type DocumentEligibilityRow = Pick<ResolveRow, "id" | "field" | "manifestDocumentValue">;

/**
 * A row is document-eligible when it has a document value and its field is
 * in DOCUMENT_WRITABLE_FIELDS. No vendorAlignmentSuspect or isValueSourced
 * check; neither applies to the document copy.
 */
export function computeDocumentEligibility(
  rows: readonly DocumentEligibilityRow[]
): DocumentEligibility {
  const eligibleIds: string[] = [];
  const skipCounts = new Map<string, number>();
  const addSkip = (reason: string) => skipCounts.set(reason, (skipCounts.get(reason) ?? 0) + 1);

  for (const row of rows) {
    if (row.manifestDocumentValue == null) {
      addSkip("has no document value");
    } else if (!DOCUMENT_WRITABLE_FIELDS.has(row.field)) {
      addSkip(`${getFieldLabel(row.field)} can't be corrected on the document`);
    } else {
      eligibleIds.push(row.id);
    }
  }

  const skips = [...skipCounts.entries()]
    .map(([reason, count]) => ({ reason, count }))
    .sort((a, b) => b.count - a.count);
  return { eligibleIds, skips };
}
